import { EncField, Encoding, SelectionMode, VariableType } from '@mode-vegas/vegas';
import { EncodingHelpers, SwitchSchemaModel, TableEncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField, Switch } from '@mode/shared/util-switch';
import { ChartCompleteData } from './chart-complete-data';

export namespace ChartViewData {
  export function encodingFromTarget(
    model: FlamingoTypes.ModeModel,
    queryToken: string,
    enc: Encoding,
    target: ChartTypes.MarkTarget
  ): Encoding {
    enc = EncodingHelpers.ensureVerticalEncoding(enc);
    const schemaModel = SwitchSchemaModel.make(model, queryToken);
    const filters =
      target.type !== ChartTypes.TargetType.VegasChart
        ? ChartCompleteData.makeFilters(schemaModel, enc, target)
        : ChartCompleteData.makeVegasFilters(schemaModel, enc, target);

    const encFilters = filters
      .filter((filter) => filter.selection.mode !== SelectionMode.ALL)
      .map((filter) => {
        return {
          ...filter,
          id: undefined,
          variable: {
            type: VariableType.DISCRETE,
          },
        };
      });

    // Add original viz filters
    const view = Switch.Views.find(model, queryToken);
    return enc.filter
      .concat(encFilters)
      .reduce(
        (encoding, filter) =>
          EncodingHelpers.addFilterToChartEncoding(encoding, filter, CompiledField.fromEncField(view, filter)).encoding,
        TableEncodingHelpers.initFilledEncoding(model, queryToken).encoding
      );
  }

  export function encodingSeriesDetail(
    model: FlamingoTypes.ModeModel,
    viewName: string,
    enc: Encoding,
    target?: ChartTypes.MarkTarget
  ): EncField[] {
    // Undefined for tabular encodings (mark channel '*')
    let seriesId = target?.series.id ?? '*';

    // If there is not a mark target, we want to get the fields of tabular encodings
    // or field of encodings with one series. If the encoding has multiple series,
    // return an empty array
    // eslint-disable-next-line no-eq-null, eqeqeq
    if (target == null) {
      // Bail if there's more than one series
      if (enc.marks.series.length > 1) {
        return [];
      }
      // id of first series
      if (enc.marks.series.length === 1) {
        seriesId = enc.marks.series[0].field;
      }
      // * for tabular
      else {
        seriesId = '*';
      }
    }

    // Model for compiling fields
    const schemaModel = SwitchSchemaModel.make(model, viewName);

    // Fields of the series
    const seriesEncFields = EncodingHelpers.collectEncFields(enc, seriesId);

    // Collect non-system dimensions (fields that affect aggregation)
    const fields: EncField[] = [];
    for (const encField of seriesEncFields) {
      const field = schemaModel.compileEncField(encField);
      if (field.isValid && !field.isSystem && field.isAggregateable) {
        fields.push(encField);
      }
    }

    return fields;
  }

  /**
   * Returns an table encoding that represents the unaggregated data underlying the given chart encoding.
   * Essentially, this just takes the filters from a chart and applies them to a table encoding with the
   * entire switch view
   * @param model
   * @param queryToken
   * @param enc A chart encoding
   * @returns A filtered table encoding
   */
  export function encodingFromChart(model: FlamingoTypes.ModeModel, queryToken: string, enc: Encoding): Encoding {
    const view = Switch.Views.find(model, queryToken);
    return enc.filter.reduce(
      (encoding, filter) =>
        EncodingHelpers.addFilterToChartEncoding(encoding, filter, CompiledField.fromEncField(view, filter)).encoding,
      TableEncodingHelpers.initFilledEncoding(model, queryToken).encoding
    );
  }
}
